import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { mediaUrl } from '../lib/sightingsApi'
import { PhotoGallery } from './PhotoGallery'
import { ProfilePhotoStrip } from './ProfilePhotoStrip'

interface Props {
  imageKeys: string[]
  alt: string
  children: ReactNode
}

export function ProfileDetailWithGallery({ imageKeys, alt, children }: Props) {
  const { t } = useTranslation()
  const [previewIndex, setPreviewIndex] = useState(0)
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null)
  const imageSignature = imageKeys.join('\0')

  useEffect(() => {
    setPreviewIndex(0)
  }, [imageSignature])

  const count = imageKeys.length
  const safeIndex =
    count === 0 ? 0 : Math.min(Math.max(0, previewIndex), count - 1)
  const currentKey = count > 0 ? imageKeys[safeIndex] : null

  return (
    <>
      <ProfilePhotoStrip
        imageKeys={imageKeys}
        alt={alt}
        previewIndex={safeIndex}
        onPreviewIndex={setPreviewIndex}
        onOpenGallery={setGalleryIndex}
      />

      <div
        className={`profile-detail-with-gallery${count === 0 ? ' no-photos' : ''}`}
      >
        <div className="profile-detail-main">{children}</div>

        {currentKey && (
          <aside
            className="profile-side-gallery"
            aria-label={t('gallery.title')}
          >
            <div className="profile-side-gallery-stage">
              {count > 1 && (
                <button
                  type="button"
                  className="profile-side-gallery-nav prev"
                  disabled={safeIndex <= 0}
                  aria-label={t('gallery.prev')}
                  onClick={() => setPreviewIndex(safeIndex - 1)}
                >
                  ‹
                </button>
              )}
              <button
                type="button"
                className="profile-side-gallery-open"
                onClick={() => setGalleryIndex(safeIndex)}
                aria-label={t('gallery.openPhoto', { number: safeIndex + 1 })}
              >
                <img src={mediaUrl(currentKey)} alt={alt} />
              </button>
              {count > 1 && (
                <button
                  type="button"
                  className="profile-side-gallery-nav next"
                  disabled={safeIndex >= count - 1}
                  aria-label={t('gallery.next')}
                  onClick={() => setPreviewIndex(safeIndex + 1)}
                >
                  ›
                </button>
              )}
            </div>

            <div className="profile-side-gallery-meta">
              {t('gallery.counter', {
                current: safeIndex + 1,
                total: count,
              })}
            </div>
          </aside>
        )}
      </div>

      {galleryIndex != null && (
        <PhotoGallery
          imageKeys={imageKeys}
          startIndex={galleryIndex}
          alt={alt}
          onClose={() => setGalleryIndex(null)}
        />
      )}
    </>
  )
}
